import React, { useMemo, useState, useEffect } from 'react';
import { formatMonthKey, parseMonthKey, getVisibleYearTicks } from './galleryFilters';

const MOBILE_MQ = '(max-width: 768px)';
const MOBILE_MAX_YEAR_LABELS = 5;
const DESKTOP_MAX_YEAR_LABELS = 12;

function useIsMobileTimeline() {
  const [isMobile, setIsMobile] = useState(() =>
    typeof window !== 'undefined' ? window.matchMedia(MOBILE_MQ).matches : false
  );

  useEffect(() => {
    const mq = window.matchMedia(MOBILE_MQ);
    const onChange = () => setIsMobile(mq.matches);
    onChange();
    mq.addEventListener('change', onChange);
    return () => mq.removeEventListener('change', onChange);
  }, []);

  return isMobile;
}

function GalleryTimeline({
  monthSteps,
  specialMonths: specialMonthsProp = [],
  activeMonthKey,
  onScrubMonth,
  isJumping = false,
}) {
  const isMobile = useIsMobileTimeline();

  const { minYear, maxYear, specialMonths, sliderIndex } = useMemo(() => {
    if (!monthSteps.length) {
      const y = new Date().getFullYear();
      return { minYear: y, maxYear: y, specialMonths: [], sliderIndex: 0 };
    }
    const years = monthSteps.map((k) => parseMonthKey(k).year);
    const minY = Math.min(...years);
    const maxY = Math.max(...years);
    const special = Array.isArray(specialMonthsProp)
      ? specialMonthsProp.filter((k) => monthSteps.includes(k))
      : [];
    const idx = Math.max(0, monthSteps.indexOf(activeMonthKey));
    return {
      minYear: minY,
      maxYear: maxY,
      specialMonths: special,
      sliderIndex: idx >= 0 ? idx : monthSteps.length - 1,
    };
  }, [monthSteps, specialMonthsProp, activeMonthKey]);

  const ticks = useMemo(
    () =>
      getVisibleYearTicks(
        minYear,
        maxYear,
        isMobile ? MOBILE_MAX_YEAR_LABELS : DESKTOP_MAX_YEAR_LABELS
      ),
    [minYear, maxYear, isMobile]
  );

  const maxIndex = Math.max(0, monthSteps.length - 1);
  const thumbPercent = maxIndex === 0 ? 0 : (sliderIndex / maxIndex) * 100;
  const thumbOffsetPx = isMobile ? 18 : 22;

  const handleSlider = (e) => {
    const i = parseInt(e.target.value, 10);
    if (monthSteps[i]) onScrubMonth(monthSteps[i]);
  };

  if (!monthSteps.length) {
    return (
      <section className="gallery-timeline" aria-label="Idővonal">
        <p className="gallery-timeline-empty">Nincs dátumozott emlék az idővonalhoz.</p>
      </section>
    );
  }

  return (
    <section
      className={`gallery-timeline${isJumping ? ' gallery-timeline--jumping' : ''}`}
      aria-label="Idővonal"
    >
      <div className="gallery-timeline-track-wrap">
        <div className="gallery-timeline-years" aria-hidden="true">
          {ticks.map((y) => (
            <span
              key={y}
              className="gallery-timeline-year"
              style={{
                left: maxYear === minYear ? '50%' : `${((y - minYear) / (maxYear - minYear)) * 100}%`,
              }}
            >
              {y}
            </span>
          ))}
        </div>

        <div className="gallery-timeline-rail">
          <div className="gallery-timeline-line" aria-hidden="true" />
          <div
            className="gallery-timeline-thumb-wrap"
            style={{ left: `calc(${thumbPercent}% - ${thumbOffsetPx}px)` }}
          >
            <span className="gallery-timeline-tooltip">
              {isJumping ? 'Ugrás…' : formatMonthKey(activeMonthKey)}
            </span>
            <span className="gallery-timeline-thumb" aria-hidden="true">
              <span className="gallery-timeline-thumb-icon">❚❚</span>
            </span>
          </div>

          {specialMonths.map((key) => {
            const i = monthSteps.indexOf(key);
            const left = maxIndex === 0 ? 50 : (i / maxIndex) * 100;
            return (
              <button
                key={key}
                type="button"
                className="gallery-timeline-heart"
                style={{ left: `${left}%` }}
                onClick={() => onScrubMonth(key)}
                title={formatMonthKey(key)}
                aria-label={`Különleges napok: ${formatMonthKey(key)}`}
              >
                ♥
              </button>
            );
          })}

          <input
            type="range"
            className="gallery-timeline-range"
            min={0}
            max={maxIndex}
            step={1}
            value={sliderIndex}
            onChange={handleSlider}
            aria-valuetext={formatMonthKey(activeMonthKey)}
            aria-label="Idővonal — húzza az időszakhoz ugráshoz"
          />
        </div>
      </div>

      <p className="gallery-timeline-tip">
        Görgessen végig az emlékek között, vagy húzza a csúszkát egy időszakhoz. A szívecskék különleges
        napokra ugranak.
      </p>
    </section>
  );
}

export default GalleryTimeline;
